<script lang="ts">
	import ClientCard from "@components/ClientCard.svelte";
	import ClientsIndustryFilter from "@components/ClientsIndustryFilter.svelte";
	import HugeHeading from "@components/HugeHeading.svelte";
	import Button from "@components/common/Button.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import { on } from "svelte/events";
	import closeIcon from "@core/assets/icons/close.svg?raw";
	import filtersIcon from "@core/assets/icons/filters.svg?raw";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { industrySearchParamLocalized } from "@core/schema/paths/industrySearchParamLocalized.js";
	import { slugify } from "@core/utils/slugify.js";
	import type { ClientListingProps } from "./ClientListingProps.js";

	const { clients, industries, tenant }: ClientListingProps = $props();

	const industrySearchParam = industrySearchParamLocalized[tenant.locale];

	const filteredIndustries = $derived(
		industries.filter((industry) => clients.some(({ industry: clientIndustry }) => clientIndustry?.id === industry.id)),
	);
	let selectedIndustryIds = $state<string[]>([]);
	const filteredClients = $derived(
		selectedIndustryIds.length === 0
			? clients
			: clients.filter((client) => client.industry !== undefined && selectedIndustryIds.includes(client.industry.id)),
	);
	const noFiltersApplied = $derived(selectedIndustryIds.length === 0);

	function applyFilterToParams(params: URLSearchParams): void {
		params.delete(industrySearchParam);
		for (const industryId of selectedIndustryIds) {
			const industry = industries.find(({ id }) => id === industryId);
			if (industry) {
				params.append(industrySearchParam, `${slugify(industry.name[tenant.locale])}-${industry.id.slice(0, 4)}`);
			}
		}
	}

	function applyParamsToFilter(): void {
		const params = new URLSearchParams(window.location.search);
		const industryIds = params.getAll(industrySearchParam);
		if (industryIds.length > 0) {
			selectedIndustryIds = industries
				.filter(({ name, id }) => industryIds.includes(`${slugify(name[tenant.locale])}-${id.slice(0, 4)}`))
				.map(({ id }) => id);
		} else {
			selectedIndustryIds = [];
		}
	}

	let changeUrlLively = $state(false);
	$effect(() => {
		void selectedIndustryIds;
		if (changeUrlLively) {
			const url = new URL(window.location.href);
			applyFilterToParams(url.searchParams);
			window.history.pushState("", "", url.toString());
		} else if (typeof window !== "undefined") {
			applyParamsToFilter();
			changeUrlLively = true;
		}
	});

	let dialog = $state<HTMLElement>();

	$effect(
		() =>
			dialog &&
			on(dialog, "click", ({ currentTarget, target }) => {
				if (currentTarget instanceof HTMLDialogElement && currentTarget === target) {
					currentTarget.close();
				}
			}),
	);

	function closeFilters(): void {
		if (typeof window === "undefined") {
			return;
		}

		if (dialog && "close" in dialog) {
			(dialog as HTMLDialogElement).close();
		}
	}

	/* eslint-disable @typescript-eslint/no-confusing-void-expression */
</script>

{#snippet filters()}
	<div class="w-full flex items-center justify-between font-semibold mb-5 lg:mb-4">
		<div class="text-base lg:text-xl/7 font-bold text-black flex items-center">
			<Button icon="none" variant={ButtonVariant.Text} class="!gap-1 lg:hidden" onclick={closeFilters}>
				<Icon icon={closeIcon} class="size-6 text-black" />
			</Button>
			<Formatted text="Filtry" />
		</div>
		<Button
			onclick={(): void => {
				selectedIndustryIds = [];
			}}
			variant={ButtonVariant.Text}
			class="text-cmcwarning !gap-1 disabled:opacity-0"
			disabled={noFiltersApplied}
		>
			<span class="uppercase text-xs tracking-[0.002em]">
				<Formatted text="Vymazat filtry" />
			</span>
			{#snippet icon()}
				<Icon icon={closeIcon} class="size-6" />
			{/snippet}
		</Button>
	</div>
	<ClientsIndustryFilter
		disabled={filteredIndustries.length === 0}
		bind:selected={selectedIndustryIds}
		industries={filteredIndustries}
	/>
{/snippet}

<svelte:window onpopstate={applyParamsToFilter} />
<Layout>
	<section class="full-width text-center pt-8 md:pt-10 lg:pt-12">
		<div class="max-w-[87.5rem] mx-auto">
			<HugeHeading level={1} text="stroke[[Klienti,]] nl[[]]kterým pomáháme s logistikou" class="mild" />
		</div>
		<div class="grid-container mt-8">
			<p
				class="max-w-[87.5rem] mx-auto text-[1.25rem]/[1.2] md:text-xl/[1.2] lg:text-[2rem]/[1.2] font-bold tracking-[0.01em] mb-8 md:mb-16 text-cmcsecondary"
			>
				<Formatted
					text="Poznejte firmy, se kterými spolupracujeme na tom, aby jejich logistika fungovala bez zbytečných starostí."
				/>
			</p>
		</div>
	</section>
	<div class="full-width text-center">
		<div class="lg:hidden flex items-center justify-center">
			<Button
				variant={ButtonVariant.Primary}
				onclick={(): void => {
					if (dialog instanceof HTMLDialogElement) {
						dialog.showModal();
					}
				}}
			>
				<Formatted text="Filtry" />
				{#snippet icon()}
					<Icon icon={filtersIcon} class="size-[2em]" />
				{/snippet}
			</Button>
		</div>
		<div class="flex py-8 md:py-16 lg:py-20 max-w-[min(1664px,100%)] mx-auto text-left w-full min-w-0 !px-2 md:!px-8">
			<div
				class="!w-[23rem] [scrollbar-gutter:stable] pr-8 mr-8 shrink-0 pt-28 pb-16 top-0 hidden lg:flex flex-col gap-2 sticky h-fit max-h-screen overflow-auto"
			>
				{@render filters()}
			</div>
			<main class="grow flex flex-col gap-12 md:gap-24">
				{#if filteredClients.length === 0}
					<div class="pt-32 font-semibold">
						<Formatted
							text={noFiltersApplied
								? "Aktuálně nejsou žádní klienti k zobrazení."
								: "Vašemu vyhledávání neodpovídají žádní klienti"}
						/>
					</div>
				{/if}
				<div class="grid gap-1 lg:gap-2 max-w-screen-xl sm:grid-cols-2 xl:grid-cols-3">
					{#each filteredClients as client (client.id)}
						<ClientCard {client} class="!w-full min-h-[13.125rem]" />
					{/each}
				</div>
			</main>
		</div>
	</div>
</Layout>

<dialog bind:this={dialog} class="top-0 m-0 w-full inset-x-0 h-fit p-0 max-w-full cmc-modal">
	<div class="flex flex-col gap-1 bg-white/75 backdrop-blur-lg p-4">
		{@render filters()}
	</div>
</dialog>
